import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { IconTrash } from '../../../../shared/components/icons/IconTrash'
import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { BpAccordion } from '../../../../shared/components/utility/accordion/BpAccordion'
import { TrackingParamConfig, usePostAffiliateRuleUpdateMutation } from '../../../../app/api'
import { hideModal, setModal } from '../../../../app/store/reducers/AppSlice'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { AddParameter } from './AddParameter'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import { useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import clsx from 'clsx'
import styles from './UpdateParams.module.scss'

interface IUrlParam {
  key: string
  value?: string
  showIgnore: boolean
  showTrack: boolean
}

interface IFormValues {
  url: string
  params: Record<
    string,
    { type: TrackingParamConfig['type']; ignored_values?: string; allowed_values?: string }
  >
  domainZones: string[]
}

type FormFieldPath = keyof IFormValues | `params.${string}` | `domainZones.${number}`

const useUrlParams = (
  getValues: () => IFormValues,
  setValue: (name: FormFieldPath, value: any) => void,
) => {
  const parseUrlParams = useCallback((url: string): IUrlParam[] => {
    try {
      const urlObj = new URL(url)
      const searchParams = new URLSearchParams(urlObj.search)
      const paramsArray: IUrlParam[] = []
      searchParams.forEach((value, key) => {
        paramsArray.push({ key, value, showIgnore: false, showTrack: false })
      })
      return paramsArray
    } catch {
      return []
    }
  }, [])

  const updateUrlWithParam = useCallback(
    (paramKey: string) => {
      const currentUrl = getValues().url
      if (!currentUrl) return

      try {
        const urlObj = new URL(currentUrl)
        const searchParams = new URLSearchParams(urlObj.search)
        searchParams.append(paramKey, '')
        urlObj.search = searchParams.toString()
        setValue('url', urlObj.toString())
      } catch {
        // If URL is invalid, do nothing
      }
    },
    [getValues, setValue],
  )

  const removeParamFromUrl = useCallback(
    (paramKey: string) => {
      const currentUrl = getValues().url
      if (!currentUrl) return

      const urlObj = new URL(currentUrl)
      const searchParams = new URLSearchParams(urlObj.search)

      searchParams.delete(paramKey)
      urlObj.search = searchParams.toString()
      setValue('url', urlObj.toString())
    },
    [getValues, setValue],
  )

  const cleanupParams = useCallback(
    (newParams: IUrlParam[]) => {
      const currentValues = getValues()
      if (currentValues.params) {
        const newParamsKeys = new Set(newParams.map((p) => p.key))
        const updatedParams = Object.entries(currentValues.params).reduce<Record<string, any>>(
          (acc, [key, value]) => {
            if (newParamsKeys.has(key)) {
              acc[key] = value
            }
            return acc
          },
          {},
        )
        setValue('params', updatedParams)
      }
    },
    [getValues, setValue],
  )

  return {
    parseUrlParams,
    updateUrlWithParam,
    removeParamFromUrl,
    cleanupParams,
  }
}

export const UpdateParams = (props: any) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [opened, setOpened] = useState(false)
  const [params, setParams] = useState<
    { key: string; value?: string; showIgnore: boolean; showTrack: boolean }[]
  >(
    props.params.map((param: any) => ({
      key: param.value,
      value: '',
      showIgnore: false,
      showTrack: false,
    })),
  )
  const [disabled, setDisabled] = useState(true)
  const [changed, setChanged] = useState(false)
  const dispatch = useAppDispatch()

  const ROLE_OPTIONS = useMemo(
    () => [
      { value: 'affiliate', text: t('pages.project.affiliateParameter') },
      { value: 'network', text: t('pages.project.networkParameter') },
      { value: 'other', text: t('pages.project.otherParameter') },
    ],
    [t],
  )

  const filteredParams = useMemo(() => {
    const utmSource = params.find((p) => p.key === 'utm_source')
    const utmCampaign = params.find((p) => p.key === 'utm_campaign')
    const otherParams = params.filter((p) => p.key !== 'utm_source' && p.key !== 'utm_campaign')

    const combined = [
      ...(utmSource ? [utmSource] : []),
      ...(utmCampaign ? [utmCampaign] : []),
      ...otherParams,
    ]

    return combined
  }, [params])

  const form = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      params: props.params.reduce((acc: any, param: any) => {
        acc[param.value] = {
          type: param.type,
          ignored_values: param.ignored_values?.join('\n'),
          allowed_values: param.allowed_values?.join('\n'),
        }
        return acc
      }, {}),
    },
  })

  const {
    register,
    watch,
    setValue,
    reset,
    getValues,
    formState: { isDirty },
  } = form

  const formValues = watch()
  const { updateUrlWithParam, removeParamFromUrl } = useUrlParams(getValues, setValue)

  const [updateParams] = usePostAffiliateRuleUpdateMutation()

  const handleAddParam = useCallback(
    (paramKey: string) => {
      setParams((prev) => [...prev, { key: paramKey, showIgnore: false, showTrack: false }])
      updateUrlWithParam(paramKey)
    },
    [updateUrlWithParam],
  )

  const handleRemoveParam = useCallback(
    (paramKey: string) => {
      setParams((prev) => prev.filter((p) => p.key !== paramKey))
      setChanged(true)
      removeParamFromUrl(paramKey)

      const values = getValues()
      const { [paramKey]: _removed, ...restParams } = values.params || {}

      reset({
        ...values,
        params: restParams,
      })
    },
    [getValues, reset, removeParamFromUrl],
  )

  const toggleIgnore = useCallback((paramKey: string) => {
    setParams((prev) =>
      prev.map((p) => (p.key === paramKey ? { ...p, showIgnore: true, showTrack: false } : p)),
    )
  }, [])

  const toggleTrack = useCallback((paramKey: string) => {
    setParams((prev) =>
      prev.map((p) => (p.key === paramKey ? { ...p, showTrack: true, showIgnore: false } : p)),
    )
  }, [])

  const submit = (runSync = false) => {
    updateParams({
      body: {
      scenario: 'update_params',
      id: props.id,
      project_id: Number(id),
      params: Object.entries(formValues.params)
        .filter(([key, value]: any) => !!value?.type)
        .map(([key, value]) => ({
          value: key,
          type: value.type,
          ignored_values: value.ignored_values?.length ? value.ignored_values?.split('\n') : [],
          allowed_values: value.allowed_values?.length ? value.allowed_values?.split('\n') : [],
        })),
      run_sync: runSync,
      },
    }).then(() => {
      dispatch(hideModal())
    })
  }

  useEffect(() => {
    setDisabled(
      formValues.params &&
        (!params.length ||
          !Object.entries(formValues.params).filter(
            ([_, value]: any) => value?.type === 'affiliate',
          ).length),
    )
  }, [formValues])

  useEffect(() => {
    if (isDirty) {
      setChanged(true)
    }
  }, [isDirty])

  return (
    <div className={styles.root}>
      <div>
        <div className={styles.panel}>
          <h3 className={styles.title}>
            {t('pages.project.editParameters')}
          </h3>
          <div className={styles.scroll}>
            <p className={styles.assignText}>
              {t('pages.project.assignRoles')}{' '}
              <Trans i18nKey='pages.project.roleExplanation'>
                <span className={styles.fontWeight600} />
              </Trans>
            </p>
            <div className={styles.tipsBlock}>
              <p className={styles.assignText}>
                💡{t('pages.project.importantTips')}{' '}
                <span
                  className={styles.tipsToggle}
                  onClick={() => setOpened((o) => !o)}
                >
                  {opened ? t('buttons.close') : t('buttons.show')}
                </span>
              </p>
              <BpAccordion opened={opened}>
                <ul className={styles.tipsList}>
                  <li>
                    <p>{t('pages.project.tipStableParams')}</p>
                  </li>
                  <li>
                    <p className={styles.tipsListItem}>
                      {t('pages.project.tipAvoidChangingParams')}
                    </p>
                  </li>
                  <li>
                    <p className={styles.tipsListItem}>
                      {t('pages.project.tipAllParamsMustMatch')}
                    </p>
                  </li>
                </ul>
                <div className={styles.tipsNested}>
                  <p className={styles.tipsListItem}>
                    {t('pages.project.tipDifferentCombinations')}
                  </p>
                  <ul className={styles.tipsDiscList}>
                    <li>utm_source + utm_campaign</li>
                    <li>utm_source + sv_utm_campaign</li>
                  </ul>
                  <p className={styles.tipsListItem}>
                    {t('pages.project.tipCreateSeparateRules')}
                  </p>
                </div>
              </BpAccordion>
            </div>
            <div className={styles.paramsBody}>
              {filteredParams.map((param) => {
                const typeValue = watch(`params.${param.key}.type`)

                return (
                  <div key={param.key} className={styles.paramCard}>
                    <div className={styles.paramCardInner}>
                      <div className={styles.paramHeader}>
                        <p className={styles.paramKey}>
                          {param.key}
                          {param.value ? `=${param.value}` : ''}
                        </p>
                        <IconButton size='lg' onClick={() => handleRemoveParam(param.key)}>
                          <IconTrash color='#00326D' />
                        </IconButton>
                      </div>
                      <div className={styles.paramFormRow}>
                        <Controller
                          key={`params.${param.key}.type`}
                          render={({ field: { onChange, ...fieldProps } }) => (
                            <BpSelect
                              {...fieldProps}
                              className={styles.selectWidth}
                              dropPosition={'auto'}
                              fieldSize='sm'
                              options={ROLE_OPTIONS}
                              placeholder={t('pages.project.selectRole')}
                              onChange={(e) => {
                                onChange(e)

                                if (e.target.value.length) {
                                  return
                                }

                                if (param.showIgnore) {
                                  setValue(`params.${param.key}.ignored_values`, '')
                                }

                                if (param.showTrack) {
                                  setValue(`params.${param.key}.allowed_values`, '')
                                }
                              }}
                            />
                          )}
                          control={form.control}
                          name={`params.${param.key}.type`}
                        />
                        <div className={styles.paramActions}>
                          <span
                            className={clsx(param.showIgnore ? styles.paramActionAqua : styles.paramActionFocus)}
                            hidden={!typeValue?.length}
                            onClick={() => toggleIgnore(param.key)}
                          >
                            {t('pages.project.ignoreCampaigns')}
                          </span>
                          <span
                            className={clsx(param.showTrack ? styles.paramActionAqua : styles.paramActionFocus)}
                            hidden={!typeValue?.length}
                            onClick={() => toggleTrack(param.key)}
                          >
                            {t('pages.project.specificCampaigns')}
                          </span>
                        </div>
                      </div>
                      {!!typeValue?.length &&
                        (param.showIgnore ? (
                          <div key={`params.${param.key}.ignored_values`}>
                            <BpTextArea
                              {...register(`params.${param.key}.ignored_values`, {
                                onChange: (e) => {
                                  setValue(
                                    `params.${param.key}.ignored_values`,
                                    e.target.value.replace(',', '\n'),
                                  )
                                },
                                onBlur: (e) => {
                                  setValue(
                                    `params.${param.key}.ignored_values`,
                                    e.target.value
                                      .split('\n')
                                      .map((value: string) => value.trim())
                                      .join('\n'),
                                  )
                                },
                              })}
                              label={t('pages.project.excludeCampaigns')}
                              placeholder={'campaign1\ncampaign2\ncampaign3'}
                            />
                            <div className={styles.helpBlock}>
                              <p className={styles.helpBlockP}>
                                {t('pages.project.containsText')}:
                                <br />
                                utm_campaign=google_ads, {t('pages.project.enter')}{' '}
                                <span className={styles.helpBlockBold}>google_ads</span>
                                <br />
                                utm_source=trusted_affiliate, {t('pages.project.enter')}{' '}
                                <span className={styles.helpBlockBold}>trusted_affiliate</span>
                              </p>
                              <p className={styles.helpBlockP}>
                                ✅ {t('other.correct')}: google_ads
                                <br />❌ {t('other.incorrect')}: utm_campaign=google_ads
                                <br />❌ {t('other.incorrect')}:
                                https://example.com/?utm_campaign=google_ads
                              </p>
                              <p>{t('pages.project.ignoreValuesNote')}</p>
                            </div>
                          </div>
                        ) : (
                          param.showTrack && (
                            <div key={`params.${param.key}.allowed_values`}>
                              <BpTextArea
                                {...register(`params.${param.key}.allowed_values`, {
                                  onChange: (e) => {
                                    setValue(
                                      `params.${param.key}.allowed_values`,
                                      e.target.value.replace(',', '\n'),
                                    )
                                  },
                                  onBlur: (e) => {
                                    setValue(
                                      `params.${param.key}.allowed_values`,
                                      e.target.value
                                        .split('\n')
                                        .map((value: string) => value.trim())
                                        .join('\n'),
                                    )
                                  },
                                })}
                                label={t('pages.project.trackCampaigns')}
                                placeholder={'campaign1\ncampaign2\ncampaign3'}
                              />
                              <div className={styles.helpBlock}>
                                <p className={styles.helpBlockP}>
                                  {t('pages.project.containsText')}:
                                  <br />
                                  utm_campaign=google_ads, {t('pages.project.enter')}{' '}
                                  <span className={styles.helpBlockBold}>google_ads</span>
                                  <br />
                                  utm_source=trusted_affiliate, {t('pages.project.enter')}{' '}
                                  <span className={styles.helpBlockBold}>trusted_affiliate</span>
                                </p>
                                <p className={styles.helpBlockP}>
                                  ✅ {t('other.correct')}: google_ads
                                  <br />❌ {t('other.incorrect')}: utm_campaign=google_ads
                                  <br />❌ {t('other.incorrect')}:
                                  https://example.com/?utm_campaign=google_ads
                                </p>
                                <p>{t('pages.project.trackValuesNote')}</p>
                              </div>
                            </div>
                          )
                        ))}
                    </div>
                  </div>
                )
              })}
              {!props.is_path_based && (
                <BpButton
                  className={styles.addParamButton}
                  contentClassName={styles.addParamContent}
                  size={'lg'}
                  theme='transparent'
                  onClick={() =>
                    dispatch(
                      setModal({
                        size: 'md',
                        children: (
                          <AddParameter
                            currentParameters={params.map((value) => value.key)}
                            onSubmit={handleAddParam}
                          />
                        ),
                      }),
                    )
                  }
                >
                  + {t('pages.project.addParameter')}
                </BpButton>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <Tooltip
          title={
            (!params.length ||
              !Object.entries(formValues.params).filter(
                ([_, value]: any) => value?.type === 'affiliate',
              ).length) &&
            'At least one must be Affiliate'
          }
        >
          <div>
            <BpButton
              className={styles.saveButton}
              disabled={disabled || !changed}
              type='button'
              onClick={() => submit()}
            >
              {t('buttons.saveChanges')}
            </BpButton>
          </div>
        </Tooltip>
        <Tooltip
          title={
            (!params.length ||
              !Object.entries(formValues.params).filter(
                ([_, value]: any) => value?.type === 'affiliate',
              ).length) &&
            'At least one must be Affiliate'
          }
        >
          <div>
            <BpButton
              className={styles.updateButton}
              disabled={props.is_syncing || disabled || !changed}
              theme={'outline'}
              type='button'
              onClick={() => submit(true)}
            >
              {t('pages.project.saveUpdateRule')}
            </BpButton>
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
